import React, { useState, useCallback, useEffect, memo } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { db as firebaseDb } from '../firebase';
import EmployeeDayView from './EmployeeDayView';
import EmployeeWeekView from './EmployeeWeekView';
import EmployeeMonthView from './EmployeeMonthView';

const EmployeeAgendaView = memo(({
  employee,
  appointments: propAppointments,
  clients,
  db = firebaseDb,
  onClose,
  hasPermission
}) => {
  const [currentView, setCurrentView] = useState('day');
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [employeeStats, setEmployeeStats] = useState({
    totalAppointments: 0,
    uniqueClients: 0,
    monthlyHours: 0,
    nextAppointment: null
  });

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Compute stats from propAppointments
  useEffect(() => {
    if (!propAppointments || !employee?.id) return;
    const employeeApts = propAppointments.filter(a => a.employeeId === employee.id);

    const uniqueClients = new Set(employeeApts.map(a => a.clientId).filter(Boolean)).size;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthApts = employeeApts.filter(a => {
      const d = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      return d >= startOfMonth;
    });
    const monthlyMin = monthApts.reduce((acc, apt) => {
      const [sh, sm] = (apt.startTime || '0:0').split(':').map(Number);
      const [eh, em] = (apt.endTime || '0:0').split(':').map(Number);
      return acc + ((eh * 60 + em) - (sh * 60 + sm));
    }, 0);

    const upcoming = employeeApts
      .filter(a => {
        const d = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        return d >= now;
      })
      .sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const db2 = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        return da - db2;
      })[0] || null;

    setEmployeeStats({
      totalAppointments: employeeApts.length,
      uniqueClients,
      monthlyHours: Math.round((monthlyMin / 60) * 10) / 10,
      nextAppointment: upcoming
    });
  }, [propAppointments, employee?.id]);

  // Navigation
  const handlePrevious = useCallback(() => {
    const d = new Date(currentDate);
    if (currentView === 'day') d.setDate(d.getDate() - 1);
    else if (currentView === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  }, [currentDate, currentView]);

  const handleNext = useCallback(() => {
    const d = new Date(currentDate);
    if (currentView === 'day') d.setDate(d.getDate() + 1);
    else if (currentView === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  }, [currentDate, currentView]);

  const handleToday = useCallback(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentDate(d);
  }, []);

  const formatDateRange = useCallback(() => {
    if (currentView === 'day') {
      return currentDate.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    if (currentView === 'month') {
      return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
    // Week range
    const start = new Date(currentDate);
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }, [currentDate, currentView]);

  if (!employee) return null;

  const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`;

  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col">

      {/* ===== HEADER ===== */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex-shrink-0">

        {/* Top row: back button + title + navigation */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              title="Retour"
            >
              <ChevronLeft size={20} className="text-gray-700 dark:text-gray-300" />
            </button>

            {/* Avatar */}
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden shadow-sm">
              {employee.photoURL
                ? <img src={employee.photoURL} alt="" className="w-full h-full object-cover" />
                : <span>{initials}</span>
              }
            </div>

            <div className="min-w-0">
              <h1 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                Planning de {employee.firstName} {employee.lastName}
              </h1>
              {employee.email && (
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{employee.email}</p>
              )}
            </div>
          </div>

          {/* Date navigation */}
          <div className="flex items-center justify-between md:justify-end space-x-2">
            <button
              onClick={handleToday}
              className="px-3 py-1.5 text-xs md:text-sm bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex-shrink-0 font-medium"
            >
              Aujourd'hui
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePrevious}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronLeft size={16} className="text-gray-700 dark:text-gray-300" />
              </button>
              <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] md:min-w-[220px] text-center px-1 truncate">
                {formatDateRange()}
              </span>
              <button
                onClick={handleNext}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronRight size={16} className="text-gray-700 dark:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats panel */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3 md:p-4 mb-4 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-3 md:gap-6">

            {/* Bénéficiaires */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-blue-100 dark:bg-blue-900/60 rounded-lg flex-shrink-0">
                <Users size={16} className="text-blue-600 dark:text-blue-300" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Bénéficiaires</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {employeeStats.uniqueClients}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {employeeStats.totalAppointments} intervention{employeeStats.totalAppointments > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Heures ce mois */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-emerald-100 dark:bg-emerald-900/60 rounded-lg flex-shrink-0">
                <Clock size={16} className="text-emerald-600 dark:text-emerald-300" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Heures ce mois</p>
                <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {employeeStats.monthlyHours}h
                </p>
              </div>
            </div>

            {/* Prochain RDV */}
            <div className="flex items-start space-x-2 md:space-x-3">
              <div className="p-1.5 md:p-2 bg-purple-100 dark:bg-purple-900/60 rounded-lg flex-shrink-0">
                <Calendar size={16} className="text-purple-600 dark:text-purple-300" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">Prochain RDV</p>
                {employeeStats.nextAppointment ? (
                  <>
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                      {employeeStats.nextAppointment.clientName || '—'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {(() => {
                        const d = employeeStats.nextAppointment.date?.toDate
                          ? employeeStats.nextAppointment.date.toDate()
                          : new Date(employeeStats.nextAppointment.date);
                        return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                      })()}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">Aucun à venir</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex space-x-1">
          {[
            { key: 'day', label: 'Jour', icon: Clock },
            { key: 'week', label: 'Semaine', icon: Calendar },
            { key: 'month', label: 'Mois', icon: Calendar }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setCurrentView(key)}
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                currentView === key
                  ? 'bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== BODY ===== */}
      <div className="flex-1 overflow-hidden">
        {currentView === 'day' && (
          <EmployeeDayView
            db={db}
            employee={employee}
            currentDate={currentDate}
            hasPermission={hasPermission}
          />
        )}
        {currentView === 'week' && (
          <EmployeeWeekView
            db={db}
            employee={employee}
            currentDate={currentDate}
            hasPermission={hasPermission}
          />
        )}
        {currentView === 'month' && (
          <EmployeeMonthView
            db={db}
            employee={employee}
            currentDate={currentDate}
            hasPermission={hasPermission}
          />
        )}
      </div>
    </div>
  );
});

EmployeeAgendaView.displayName = 'EmployeeAgendaView';

export default EmployeeAgendaView;
